import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import '../Customize_Css/Dashboard.css';
import Vendor from '../Icons/Frame.svg';
import Order from '../Icons/order.png';
import Sales from '../Icons/sales.png';
import Pending from '../Icons/pending.png';
import LineGraph from '../Components/LineGraph';
import OrderDataTable from '../Components/OrderDataTable';
import Sidebar from '../Components/Siderbar';
import Footer from '../Components/Footer';
import { Pie } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import {
  FaClipboardCheck,
  FaCheckCircle,
  FaTruckPickup,
  FaBoxOpen,
  FaMapMarkerAlt,
  FaBox,
  FaTruck,
  FaHome,
  FaTimesCircle,
  FaWallet,
  FaCalendarDay,
  FaMoneyBillWave,
  FaCreditCard,
  FaMobileAlt
} from "react-icons/fa";

ChartJS.register(ArcElement, Tooltip, Legend);

const STATUS_COLORS = {
  'order-placed': '#0f9f86',
  'order-confirmed': '#14b8a6',
  'on-the-way-to-pickup': '#0284c7',
  'order-getting-prepared': '#6366f1',
  'arrived-at-location': '#8b5cf6',
  'package-picked': '#d97706',
  'on-the-way-to-delivery': '#0ea5e9',
  'order-delivered': '#16a34a',
  'order-cancelled': '#e11d48'
};

const NETWORK_COLORS = ['#0f9f86', '#0284c7', '#7c3aed', '#d97706'];

const pieOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { display: false },
    tooltip: {
      callbacks: {
        label(context) {
          const values = context.dataset.data || [];
          const total = values.reduce((sum, value) => sum + value, 0);
          const value = context.parsed;
          const pct = total ? Math.round((value / total) * 100) : 0;
          return ` ${context.label}: ${value} (${pct}%)`;
        }
      }
    }
  }
};

const StatCard = ({ title, count, iconSrc, Icon, tone = 'teal', onClick }) => (
  <article
    className={`krv-stat-card krv-tone-${tone}${onClick ? ' krv-stat-card--clickable' : ''}`}
    onClick={onClick}
    role={onClick ? 'button' : undefined}
    tabIndex={onClick ? 0 : undefined}
    onKeyDown={onClick ? (e) => (e.key === 'Enter' || e.key === ' ') && onClick() : undefined}
  >
    <div className="krv-stat-icon" aria-hidden="true">
      {Icon ? (
        <Icon size={18} />
      ) : (
        iconSrc && <img src={iconSrc} alt="" />
      )}
    </div>
    <div className="krv-stat-copy">
      <p className="krv-stat-label">{title}</p>
      <p className="krv-stat-value">{count ?? 0}</p>
    </div>
  </article>
);

const SitePie = ({ slices, emptyText }) => {
  const total = slices.reduce((sum, slice) => sum + (Number(slice.value) || 0), 0);
  const drawn = slices.filter((slice) => Number(slice.value) > 0);

  if (!drawn.length) {
    return <div className="krv-empty">{emptyText}</div>;
  }

  const data = {
    labels: drawn.map((slice) => slice.label),
    datasets: [
      {
        data: drawn.map((slice) => Number(slice.value) || 0),
        backgroundColor: drawn.map((slice) => slice.color),
        borderColor: '#ffffff',
        borderWidth: 2,
        hoverOffset: 6
      }
    ]
  };

  return (
    <div className="krv-pie-layout">
      <div className="krv-pie-canvas">
        <Pie data={data} options={pieOptions} />
      </div>
      <ul className="krv-legend">
        {slices.map((slice) => {
          const value = Number(slice.value) || 0;
          const pct = total ? Math.round((value / total) * 100) : 0;
          return (
            <li key={slice.label}>
              <span className="krv-dot" style={{ background: slice.color }} />
              <span>{slice.label}</span>
              <span className="krv-legend-count">{value}</span>
              <span className="krv-legend-pct">{pct}%</span>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

const Dashboard = () => {
  const navigate = useNavigate();
  const epicxTenantId = localStorage.getItem('epicxTenantId');
  const BASE_URL = process.env.REACT_APP_BASE_URL;
  const AuthorizationToken = localStorage.getItem('token');

  const NETWORK_ROUTES = {
    'Total Product Vendors': `/viewvendor/${epicxTenantId}`,
    'Total Delivery Vendors': '/view-delivery-vendor',
    'Total Delivery Vendor Agents': '/view-delivery-vendor-agents',
    'Total Independent Delivery Agents': '/view-independent-delivery',
    'Total New Orders': '/view-orders',
  };
  const [cards, setCards] = useState([
    { id: 1, title: 'Total Product Vendors', count: 0, iconSrc: Vendor, tone: 'teal' },
    { id: 2, title: 'Total Delivery Vendors', count: 0, iconSrc: Order, tone: 'sky' },
    { id: 3, title: 'Total Delivery Vendor Agents', count: 0, iconSrc: Order, tone: 'violet' },
    { id: 4, title: 'Total Independent Delivery Agents', count: 0, iconSrc: Sales, tone: 'amber' },
    { id: 5, title: 'Total New Orders', count: 0, iconSrc: Pending, tone: 'green' },
  ]);
  const paymentDetails = [
    { id: 6, title: 'Total Revenue', count: 0, Icon: FaWallet, tone: 'teal' },
    { id: 7, title: 'Today Revenue', count: 0, Icon: FaCalendarDay, tone: 'green' },
    { id: 8, title: 'Cash on Delivery Count', count: 0, Icon: FaMoneyBillWave, tone: 'amber' },
    { id: 9, title: 'Online Delivery Count', count: 0, Icon: FaCreditCard, tone: 'sky' },
    { id: 10, title: 'UPI Transaction Count', count: 0, Icon: FaMobileAlt, tone: 'violet' },
  ];

const [orderStatusCards, setOrderStatusCards] = useState([
  { id: 11, key: "order-placed", title: "Order Placed", count: 0, icon: FaClipboardCheck, tone: 'teal' },
  { id: 12, key: "order-confirmed", title: "Order Confirmed", count: 0, icon: FaCheckCircle, tone: 'sky' },
  { id: 13, key: "on-the-way-to-pickup", title: "On the Way to Pickup", count: 0, icon: FaTruckPickup, tone: 'violet' },
  { id: 14, key: "order-getting-prepared", title: "Order Getting Prepared", count: 0, icon: FaBoxOpen, tone: 'amber' },
  { id: 15, key: "arrived-at-location", title: "Arrived at Location", count: 0, icon: FaMapMarkerAlt, tone: 'sky' },
  { id: 16, key: "package-picked", title: "Package Picked", count: 0, icon: FaBox, tone: 'teal' },
  { id: 17, key: "on-the-way-to-delivery", title: "On the Way to Delivery", count: 0, icon: FaTruck, tone: 'violet' },
  { id: 18, key: "order-delivered", title: "Order Delivered", count: 0, icon: FaHome, tone: 'green' },
  { id: 19, key: "order-cancelled", title: "Order Cancelled", count: 0, icon: FaTimesCircle, tone: 'rose' }
]);


  // Reusable fetch function with authorization headers
  const fetchWithAuth = (url) => {
    if (!AuthorizationToken) {
      console.error('No authorization token found');
      return Promise.reject('No authorization token');
    }
    return fetch(url, {
      headers: {
        'Authorization': `Bearer ${AuthorizationToken}`,
        'Content-Type': 'application/json'
      }
    }).then(response => {
      if (response.status === 401) {
        // Handle unauthorized access (e.g., redirect to login)
        throw new Error('Unauthorized');
      }
      return response;
    });
  };

  useEffect(() => {
  // Fetch Total Product Vendors
  fetchWithAuth(`${BASE_URL}/vendor/api/vendor/${epicxTenantId}/productVendorCount`)
    .then(response => {
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return response.json();
    })
    .then(data => {
      const count = data?.productVendorsCount ?? data?.count ?? data?.totalProductVendors ?? 0;
      setCards(prevCards =>
        prevCards.map(card =>
          card.title === 'Total Product Vendors'
            ? { ...card, count }
            : card
        )
      );
      console.log('Product Vendors Count:', count, data);
    })
    .catch(error => {
      console.error('Error fetching Product Vendors:', error);
    });
    // Fetch Total Delivery Vendors
    fetchWithAuth(`${BASE_URL}/deliveryVendor/api/deliveryVendor/${epicxTenantId}/deliveryVendorCounts`)
      .then(response => response.json())
      .then(data => {
        setCards(prevCards =>
          prevCards.map(card =>
            card.title === 'Total Delivery Vendors'
              ? { ...card, count: data.activeVendorsCount }
              : card
          )
        );
      })
      .catch(error => console.error('Error fetching Delivery Vendors:', error));

    // Fetch Total Delivery Vendor Agents
    fetchWithAuth(`${BASE_URL}/deliveryVendor/api/deliveryVendorAgents/${epicxTenantId}/deliveryVendorAgentsCount`)
      .then(response => response.json())
      .then(data => {
        setCards(prevCards =>
          prevCards.map(card =>
            card.title === 'Total Delivery Vendor Agents'
              ? { ...card, count: data.activeAgentsCount }
              : card
          )
        );
      })
      .catch(error => console.error('Error fetching Delivery Vendor Agents:', error));

    // Fetch Total Independent Deliveries
    fetchWithAuth(`${BASE_URL}/independentDelivery/api/independentDelivery/${epicxTenantId}/independentDeliveriesCount`)
      .then(response => response.json())
      .then(data => {
        setCards(prevCards =>
          prevCards.map(card =>
            card.title === 'Total Independent Delivery Agents'
              ? { ...card, count: data.activeDeliveriesCount }
              : card
          )
        );
      })
      .catch(error => console.error('Error fetching Independent Deliveries:', error));

    // Fetch Total New Orders
    fetchWithAuth(`${BASE_URL}/orders/api/orders/${epicxTenantId}/newOrdersCount`)
      .then(response => response.json())
      .then(data => {
        setCards(prevCards =>
          prevCards.map(card =>
            card.title === 'Total New Orders'
              ? { ...card, count: data.orderPlacedCount }
              : card
          )
        );
      })
      .catch(error => console.error('Error fetching New Orders:', error));
  }, [AuthorizationToken]);

  // Orders status count
useEffect(() => {
  const fetchOrderCounts = async () => {
    try {
      const response = await fetchWithAuth(`${BASE_URL}/orders/api/orders/${epicxTenantId}/ordersCount`);
      const data = await response.json();

      const statusMap = data.statusCounts.reduce((acc, item) => {
        const normalizedKey = item.status
          .toLowerCase()
          .replace(/\s+/g, '-');

        acc[normalizedKey] = item.count;
        return acc;
      }, {});

      const updatedCards = orderStatusCards.map(card => ({
        ...card,
        count: statusMap[card.key] || 0
      }));

      setOrderStatusCards(updatedCards);
    } catch (error) {
      console.error("Error fetching order status counts:", error);
    }
  };

  fetchOrderCounts();
}, [AuthorizationToken]);

  const pipelineTotal = orderStatusCards.reduce(
    (sum, card) => sum + (Number(card.count) || 0),
    0
  );

  const orderSlices = orderStatusCards.map((card) => ({
    label: card.title,
    value: Number(card.count) || 0,
    color: STATUS_COLORS[card.key] || '#0f9f86'
  }));

  const networkSlices = cards
    .filter((card) => card.id !== 5)
    .map((card, index) => ({
      label: card.title.replace(/^Total\s/, ''),
      value: Number(card.count) || 0,
      color: NETWORK_COLORS[index] || '#0f9f86'
    }));

  return (
    <>
      <Sidebar />
      <div className="kravez-dashboard">
        <div className="krv-dash-head">
          <div>
            <p className="krv-kicker">Overview</p>
            <h1>Dashboard</h1>
            <p>Vendors, deliveries, and orders in one place.</p>
          </div>
        </div>

        <section className="krv-section">
          <div className="krv-section-head">
            <h2>Network</h2>
            <span>Vendors, agents, and new orders</span>
          </div>
          <div className="krv-stat-grid">
            {cards.map(card => (
              <StatCard
                key={card.id}
                title={card.title}
                count={card.count}
                iconSrc={card.iconSrc}
                tone={card.tone}
                onClick={NETWORK_ROUTES[card.title] ? () => navigate(NETWORK_ROUTES[card.title]) : undefined}
              />
            ))}
          </div>
        </section>

        <section className="krv-section">
          <div className="krv-section-head">
            <h2>Order pipeline</h2>
            <span>{pipelineTotal} orders across every status</span>
          </div>
          <div className="krv-status-grid">
            {orderStatusCards.map((card) => (
              <StatCard
                key={card.id}
                title={card.title}
                count={card.count}
                Icon={card.icon}
                tone={card.tone}
                onClick={() => navigate('/view-orders', { state: { statusFilter: card.key } })}
              />
            ))}
          </div>
        </section>

        <section className="krv-section">
          <div className="krv-section-head">
            <h2>Payments</h2>
            <span>Revenue and transaction counts</span>
          </div>
          <div className="krv-stat-grid">
            {paymentDetails.map(card => (
              <StatCard
                key={card.id}
                title={card.title}
                count={card.count}
                Icon={card.Icon}
                tone={card.tone}
              />
            ))}
          </div>
        </section>

        <section className="krv-section">
          <div className="krv-section-head">
            <h2>Site snapshot</h2>
            <span>Live mix of the numbers above</span>
          </div>
          <div className="krv-chart-grid">
            <article className="krv-panel">
              <h3>Order status</h3>
              <p>Share of orders in each stage.</p>
              <SitePie
                slices={orderSlices}
                emptyText="No order activity yet. Statuses will appear here once orders come in."
              />
            </article>
            <article className="krv-panel">
              <h3>Vendors and agents</h3>
              <p>How the delivery network is split.</p>
              <SitePie
                slices={networkSlices}
                emptyText="Vendor and agent counts will appear here once they are available."
              />
            </article>
          </div>
          <article className="krv-panel">
            <h3>Monthly trend</h3>
            <p>Revenue movement across the year.</p>
            <div className="krv-line-body">
              <LineGraph />
            </div>
          </article>
        </section>

        <section className="krv-orders">
          <OrderDataTable />
        </section>
        <Footer />
      </div>
    </>
  );
};

export default Dashboard;
